import React, { useState, useRef } from 'react';
import { EventType } from '../../types/Events';
import Select from 'react-select';
import { colourStyles } from '../../utils/customSelectStyle';
import { eventTime } from '../../utils/constant';
import { SelectOptionType } from '../../types/Common';
import { updateEvent } from '../../api';
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; 
import { useNavigate } from "react-router-dom";
import ImageCropper from '../../components/common/ImageCropper/ImageCropper';
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import CustomDatePicker from '../../components/common/CustomDatePicker/CustomDatePicker';
import CustomTimePicker from '../../components/common/CustomTimePicker/CustomTimePicker';
import CustomSelect from '../../components/common/CustomSelect/CustomSelect';
import { FileText, Image as ImageIcon, MapPin, Settings } from 'lucide-react';

type EditEventTabProps = {
    handleChange: (e: any) => void;
    formData: EventType;
    onSelectChange: (val: any, key: string) => void;
    onCheckboxChange: (key: string, checked: boolean) => void;
    selectedFile: any;
    handleThumbBtnClick: () => void;
    fileInputRef: any;
    handleFileChange: (e: any) => void;
    countries: SelectOptionType[];
    categories: SelectOptionType[];
};

const EditEventTab = (props: EditEventTabProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [croppedImage, setCroppedImage] = useState<File | null>(null);
  const [fileToCrop, setFileToCrop] = useState<File | null>(null);
  const navigate = useNavigate();
  const [selectedTicketFile, setSelectedTicketFile] = useState<File | null>(null);
  const ticketInputRef = useRef<HTMLInputElement>(null);
  
  const {
      handleChange,
      formData,
      onSelectChange,
      onCheckboxChange,
      selectedFile,
      handleThumbBtnClick,
      fileInputRef,
  } = props;

  const onFileSelect = (e: any) => {
    const file = e.target.files?.[0];
    if (file) {
      setFileToCrop(file);
      setIsCropperOpen(true);
      e.target.value = "";
    }
  };

  const onTicketFileSelect = (e: any) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedTicketFile(file);
      e.target.value = "";
    }
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      
      if (!formData.hasMultipleVenues && !formData.eventVenue) {
        toast.error("Please select an event location");
        setIsLoading(false);
        return;
      }

      const formDataToSubmit = new FormData();

      const cleanDate = (d: any): string | null => {
        if (!d || d === 'Invalid date' || d === '' || d === 'null') return null;
        return d;
      };

      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'endDate' || key === 'eventDate') return;
        if (value === null || value === undefined || value === 'Invalid date') return;
        formDataToSubmit.append(key, value as any);
      });

      const safeEventDate = cleanDate(formData.eventDate);
      const safeEndDate = formData.isSingleDayEvent ? safeEventDate : (cleanDate(formData.endDate) || safeEventDate);

      if (safeEventDate) formDataToSubmit.append('eventDate', safeEventDate);
      if (safeEndDate) formDataToSubmit.append('endDate', safeEndDate);

      formDataToSubmit.set('isSingleDayEvent', String(!!formData.isSingleDayEvent));
      formDataToSubmit.set('isSameDayEvent', String(!!formData.isSingleDayEvent));

      if (croppedImage) {
        formDataToSubmit.append('thumbImg', croppedImage);
      } else if (selectedFile) {
        formDataToSubmit.append('thumbImg', selectedFile);
      }

      if (selectedTicketFile) {
        formDataToSubmit.append('ticketImage', selectedTicketFile);
      }

      const venueDetails = {
        name: formData.eventVenue || "",
        venueName: formData.venueName || "",
        addressLine1: formData.eventAddress1 || "",
        addressLine2: formData.eventAddress2 || "",
        country: formData.eventCountry || "",
        state: formData.eventState || "",
        city: formData.eventCity || "",
        zipCode: formData.eventZip || "",
        latitude: formData.latitude || 0,
        longitude: formData.longitude || 0,
      };
      
      formDataToSubmit.set("eventLocation", venueDetails.name);
      formDataToSubmit.set("eventVenue", venueDetails.name);
      formDataToSubmit.set("eventAddress1", venueDetails.name);
      
      formDataToSubmit.append("venueDetails", JSON.stringify(venueDetails));

      const locationData = [{
        ...venueDetails,
        formattedAddress: formData.eventLocation || formData.eventAddress1 || ""
      }];
      formDataToSubmit.append("locationData", JSON.stringify(locationData));

      await updateEvent(formData.eventId, formDataToSubmit);
      toast.success("Event updated successfully");

      setTimeout(() => {
        const eventData = localStorage.getItem("eventData");
        if (eventData) {
          const eventId = JSON.parse(eventData).eventId;
          navigate(`/event/${eventId}`);
        }
      }, 2000);
    } catch (error: any) {
      console.error("Error updating event:", error);
      toast.error("Failed to update event");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* 1. Event Information */}
      <div className="glass-panel" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="v3-section-head">
          <FileText size={14} color="#EE3F7C" /> Event Information
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label className="v3-label">Event Name *</label>
            <input
              className="v3-input"
              type="text"
              placeholder="Enter event name here"
              name="eventName"
              onChange={handleChange}
              value={formData.eventName || ''}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 16 }}>
            <div>
              <label className="v3-label">Duration Structure</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => {
                    onCheckboxChange("isSingleDayEvent", true);
                    handleChange({ target: { name: "endDate", value: "" } });
                  }}
                  style={{
                    flex: 1, padding: '8px 0', fontSize: 11, fontWeight: 700,
                    borderRadius: 8, cursor: 'pointer', transition: 'all 0.15s',
                    background: formData.isSingleDayEvent ? 'rgba(238, 63, 124, 0.15)' : 'var(--v3-border-subtle)',
                    border: formData.isSingleDayEvent ? '1px solid #EE3F7C' : '1px solid var(--v3-border-subtle)',
                    color: formData.isSingleDayEvent ? 'var(--v3-text-main)' : 'var(--v3-text-muted)',
                  }}
                >Single-Day</button>
                <button
                  type="button"
                  onClick={() => onCheckboxChange("isSingleDayEvent", false)}
                  style={{
                    flex: 1, padding: '8px 0', fontSize: 11, fontWeight: 700,
                    borderRadius: 8, cursor: 'pointer', transition: 'all 0.15s',
                    background: !formData.isSingleDayEvent ? 'rgba(238, 63, 124, 0.15)' : 'var(--v3-border-subtle)',
                    border: !formData.isSingleDayEvent ? '1px solid #EE3F7C' : '1px solid var(--v3-border-subtle)',
                    color: !formData.isSingleDayEvent ? 'var(--v3-text-main)' : 'var(--v3-text-muted)',
                  }}
                >Multi-Day</button>
              </div>
            </div>

            <div>
              <label className="v3-label">{formData.isSingleDayEvent ? 'Event Date *' : 'Start Date *'}</label>
              <CustomDatePicker 
                value={formData.eventDate || ''} 
                onChange={(val) => handleChange({ target: { name: 'eventDate', value: val } })} 
                placeholder="Select date" 
              />
            </div>

            {!formData.isSingleDayEvent && (
              <div>
                <label className="v3-label">End Date *</label>
                <CustomDatePicker 
                  value={formData.endDate || ''} 
                  onChange={(val) => handleChange({ target: { name: 'endDate', value: val } })} 
                  placeholder="Select date" 
                />
              </div>
            )}

            <div>
              <label className="v3-label">Start Time</label>
              <CustomTimePicker 
                value={formData.eventTime || ''} 
                onChange={(val) => onSelectChange({ value: val }, 'eventTime')} 
                placeholder="Select time" 
              />
            </div>

            <div>
              <label className="v3-label">End Time</label>
              <CustomTimePicker 
                value={formData.endTime || ''} 
                onChange={(val) => onSelectChange({ value: val }, 'endTime')} 
                placeholder="Select time" 
              />
            </div>
          </div>
        </div>
      </div>

      {/* 2. Banner & Visuals */}
      <div className="glass-panel" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="v3-section-head">
          <ImageIcon size={14} color="#EE3F7C" /> Banner & Visuals
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 20 }}>
          {/* Event Banner */}
          <div>
            <label className="v3-label">Event Banner (3:4 Aspect Ratio)</label>
            <div 
              className="v3-poster-box"
              onClick={handleThumbBtnClick}
              style={{
                width: '100%', maxWidth: 220, height: 280, borderRadius: 14,
                border: '1px dashed var(--v3-border-medium)', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                position: 'relative', overflow: 'hidden', background: 'var(--v3-bg-subtle)'
              }}
            >
              <input
                type="file"
                id="thumbImg"
                ref={fileInputRef}
                onChange={onFileSelect}
                style={{ display: "none" }}
                accept="image/jpeg, image/png"
              />
              {!(croppedImage || selectedFile || formData.thumbImg) ? (
                <div style={{ textAlign: 'center', padding: 12 }}>
                  <ImageIcon size={32} color="#EE3F7C" style={{ marginBottom: 8 }} />
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--v3-text-main)' }}>Upload Banner</div>
                  <div style={{ fontSize: 10, color: 'var(--v3-text-muted)' }}>3:4 Aspect Ratio</div>
                </div>
              ) : (
                <img
                  src={
                    croppedImage
                      ? URL.createObjectURL(croppedImage)
                      : selectedFile
                        ? URL.createObjectURL(selectedFile)
                        : formData.thumbImg
                  }
                  alt="Event banner"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              )}
            </div>

            <button
              className="v3-btn-secondary mt-2"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleThumbBtnClick();
              }}
              style={{ fontSize: 12, padding: '6px 14px' }}
            >
              Change Banner
            </button>
          </div>

          {/* Ticket Image */}
          <div>
            <label className="v3-label">Ticket Image (16:9 Aspect Ratio)</label>
            <div 
              className="v3-poster-box"
              onClick={() => ticketInputRef.current?.click()}
              style={{
                width: '100%', maxWidth: 320, height: 180, borderRadius: 14,
                border: '1px dashed var(--v3-border-medium)', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                position: 'relative', overflow: 'hidden', background: 'var(--v3-bg-subtle)'
              }}
            >
              <input
                type="file"
                id="ticketImage"
                ref={ticketInputRef}
                onChange={onTicketFileSelect}
                style={{ display: "none" }}
                accept="image/jpeg, image/png"
              />
              {!(selectedTicketFile || formData.ticketImage) ? (
                <div style={{ textAlign: 'center', padding: 12 }}>
                  <ImageIcon size={32} color="#EE3F7C" style={{ marginBottom: 8 }} />
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--v3-text-main)' }}>Upload Ticket Image</div>
                  <div style={{ fontSize: 10, color: 'var(--v3-text-muted)' }}>16:9 Aspect Ratio</div>
                </div>
              ) : (
                <img
                  src={
                    selectedTicketFile
                      ? URL.createObjectURL(selectedTicketFile)
                      : formData.ticketImage
                  }
                  alt="Ticket banner"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              )}
            </div>

            <button
              className="v3-btn-secondary mt-2"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                ticketInputRef.current?.click();
              }}
              style={{ fontSize: 12, padding: '6px 14px' }}
            >
              Change Ticket Image
            </button>
          </div>
        </div>

        {/* Cropper Modal */}
        {isCropperOpen && fileToCrop && (
          <div className="cropper-modal-overlay">
            <div className="cropper-modal-card">
              <ImageCropper
                imageFile={fileToCrop}
                aspect={3 / 4}
                onCancel={() => setIsCropperOpen(false)}
                onCropComplete={(croppedFile) => {
                  setCroppedImage(croppedFile);
                  setIsCropperOpen(false);
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* 3. Description & Details */}
      <div className="glass-panel" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="v3-section-head">
          <FileText size={14} color="#EE3F7C" /> Description & Details
        </div>
        <div>
          <label className="v3-label">Short Description</label>
          <input
            className="v3-input"
            placeholder="Enter short description here"
            type="text"
            name="shortDescription"
            onChange={handleChange}
            value={formData.shortDescription || ''}
          />
        </div>
        <div>
          <label className="v3-label">Event Description</label>
          <div className="mt-2">
            <ReactQuill
              value={formData.eventDescription || ''} 
              onChange={(value) =>
                handleChange({
                  target: { name: "eventDescription", value },
                })
              }
              className="text-editor"
            />
          </div>
        </div>
      </div>

      {/* 4. Event Location */}
      {!formData.hasMultipleVenues && (
        <div className="glass-panel" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="v3-section-head">
            <MapPin size={14} color="#EE3F7C" /> Event Location
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label className="v3-label">Venue Name</label>
              <input
                className="v3-input"
                type="text"
                placeholder="Enter venue name"
                name="venueName"
                value={formData.venueName || ''}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="v3-label">Search Location Address</label>
              <GooglePlacesAutocomplete
                apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""}
                selectProps={{
                  placeholder: "Search event location...",
                  isClearable: true,
                  value: formData.eventVenue
                    ? { label: formData.eventVenue, value: formData.eventVenue }
                    : null,
                  onChange: (place: any) => {
                    if (!place) {
                      handleChange({ target: { name: "eventVenue", value: "" } });
                      handleChange({ target: { name: "eventCountry", value: 0 } });
                      handleChange({ target: { name: "eventState", value: "" } });
                      handleChange({ target: { name: "eventCity", value: "" } });
                      handleChange({ target: { name: "eventZip", value: "" } });
                      handleChange({ target: { name: "eventAddress1", value: "" } });
                      handleChange({ target: { name: "eventLocation", value: "" } });
                      handleChange({ target: { name: "latitude", value: 0 } });
                      handleChange({ target: { name: "longitude", value: 0 } });
                      return;
                    }

                    handleChange({
                      target: {
                        name: "eventVenue",
                        value: place.label,
                      },
                    });

                    const placeId = place.value?.place_id;
                    if (!placeId) return;

                    const geocoder = new google.maps.Geocoder();

                    geocoder.geocode({ placeId }, (results, status) => {
                      if (status !== "OK" || !results?.[0]) return;

                      const components = results[0].address_components;

                      const getComponent = (type: string) =>
                        components.find((c) => c.types.includes(type))?.long_name || "";

                      const countryName = getComponent("country");
                      const state = getComponent("administrative_area_level_1");
                      const city =
                        getComponent("locality") ||
                        getComponent("administrative_area_level_2");
                      const zip = getComponent("postal_code");

                      const matchedCountry = props.countries.find(
                        (c) => c.label.toLowerCase() === countryName.toLowerCase()
                      );

                      if (matchedCountry) {
                        handleChange({
                          target: {
                            name: "eventCountry",
                            value: matchedCountry.value,
                          },
                        });
                      }

                      const formattedAddress = results[0].formatted_address;
                      handleChange({ target: { name: "eventAddress1", value: formattedAddress } });
                      handleChange({ target: { name: "eventLocation", value: formattedAddress } });
                      handleChange({ target: { name: "eventState", value: state } });
                      handleChange({ target: { name: "eventCity", value: city } });
                      handleChange({ target: { name: "eventZip", value: zip } });

                      const lat = results[0].geometry.location.lat();
                      const lng = results[0].geometry.location.lng();
                      handleChange({ target: { name: "latitude", value: lat } });
                      handleChange({ target: { name: "longitude", value: lng } });
                    });
                  },
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* 5. Event Settings */}
      <div className="glass-panel" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="v3-section-head">
          <Settings size={14} color="#EE3F7C" /> Event Settings
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Event Status */}
          <div>
            <label className="v3-label">Event Status</label>
            <CustomSelect
              value={formData.eventStatus || "draft"}
              options={[
                { value: "draft", label: "Draft" },
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
                { value: "cancelled", label: "Cancelled" },
                { value: "closed", label: "Closed" }
              ]}
              onChange={(val: string) =>
                onSelectChange({ value: val }, "eventStatus")
              }
            />
            <small style={{ color: 'var(--v3-text-muted)', fontSize: 11, marginTop: 4, display: 'block' }}>
              Set the current status of your event. Published events are visible to attendees.
            </small>
          </div>
          
          {/* Require information for all attendees */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, paddingTop: 12, borderTop: '1px solid var(--v3-border-subtle)' }}>
            <label className="btn-switch m-0 flex-shrink-0">
              <input
                type="checkbox"
                checked={formData.isOtherAttendeeRequired || false}
                onChange={(e) => onCheckboxChange('isOtherAttendeeRequired', e.target.checked)}
              />
              <span className="checkbox-slider"></span>
            </label>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--v3-text-main)' }}>Require information for all attendees</div>
              <div style={{ fontSize: 11, color: 'var(--v3-text-muted)', marginTop: 2 }}>When enabled, customers must provide information for each ticket holder. When disabled, only the primary attendee information is required.</div>
            </div>
          </div>

          {/* Terms & Conditions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 12, borderTop: '1px solid var(--v3-border-subtle)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <label className="btn-switch m-0 flex-shrink-0">
                <input
                  type="checkbox"
                  checked={formData.hasTermsAndConditions || false}
                  onChange={(e) => onCheckboxChange('hasTermsAndConditions', e.target.checked)}
                />
                <span className="checkbox-slider"></span>
              </label>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--v3-text-main)' }}>Add Terms & Conditions for ticket booking</div>
                <div style={{ fontSize: 11, color: 'var(--v3-text-muted)', marginTop: 2 }}>When enabled, customers must agree to your terms and conditions before completing their booking.</div>
              </div>
            </div>

            {formData.hasTermsAndConditions && (
              <div className="mt-2">
                <ReactQuill
                  className="text-editor mt-2"
                  value={formData.termsAndConditions || ''}
                  onChange={(value) => {
                    handleChange({
                      target: { name: "termsAndConditions", value },
                    });
                  }}
                />
              </div>
            )}
          </div>

          {/* Pre-Registration Required */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, paddingTop: 12, borderTop: '1px solid var(--v3-border-subtle)' }}>
            <label className="btn-switch m-0 flex-shrink-0">
              <input
                type="checkbox"
                checked={formData.isPreRegistrationRequired || false}
                onChange={(e) =>
                  onCheckboxChange("isPreRegistrationRequired", e.target.checked)
                }
              />
              <span className="checkbox-slider"></span>
            </label>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--v3-text-main)' }}>Is Pre‑Registration Required?</div>
              <div style={{ fontSize: 11, color: 'var(--v3-text-muted)', marginTop: 2 }}>When enabled, users must pre‑register before they can book tickets.</div>
            </div>
          </div>
        </div>
      </div>

      {/* 6. Submit Button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <button
          type="button"
          className="v3-btn-primary"
          onClick={handleSubmit}
          disabled={isLoading}
          style={{ minWidth: 200, padding: '12px 28px', fontSize: 14 }}
        >
          {isLoading ? "Submitting..." : "Update Event"}
        </button>
      </div>

      <ToastContainer autoClose={2000} />
    </div>
  ); 
};

export default EditEventTab;
